<!-- Login.vue -->
<template>
  <div class="bg">
    <v-container fill-height fluid>
      <v-row align="center">
        <v-col>
          <v-card
            class="mx-auto"
            max-width="1000"
            min-height="565"
            align="right"
            :id="'main_login_image' + selected_image"
          >
            <v-card
              max-width="350"
              min-height="565"
              align="center"
              class="rounded-l-0"
              flat
            >
              <v-container>
                <v-img
                  class="mt-1"
                  v-if="$vuetify.theme.dark"
                  src="@/assets/LogoV2-White_ClearBknd.png"
                  max-height="100"
                  max-width="200"
                ></v-img>
                <v-img
                  class="mt-1"
                  v-if="!$vuetify.theme.dark"
                  src="@/assets/LogoV2-Black_ClearBknd.png"
                  max-height="100"
                  max-width="200"
                ></v-img>
              </v-container>

              <!-- Login Form -->
              <v-container v-if="login_rp === 'login' && !loading">
                <span
                  class="d-flex justify-center primary_blue--text mb-10 mt-n2"
                  style="font-size: 22px"
                >
                  Elanco Portal
                </span>

                <p>Login to your account</p>

                <v-form
                  ref="form-login"
                  @submit.prevent="handleLogin"
                  v-model="isLoginFormValid"
                >
                  <v-text-field
                    v-model="email"
                    label="Email"
                    class="mx-2"
                    outlined
                    type="email"
                    clearable
                    color="primary_blue"
                    :rules="[validateEmail]"
                    data-cy="field_email"
                  />

                  <v-text-field
                    v-model="password"
                    label="Password"
                    class="mx-2"
                    outlined
                    color="primary_blue"
                    clearable
                    :append-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append="() => (visible = !visible)"
                    :type="visible ? 'text' : 'password'"
                    :rules="[validateNotEmpty]"
                    data-cy="field_password"
                  />

                  <v-btn
                    elevation="4"
                    large
                    color="primary_blue"
                    type="submit"
                    :disabled="!isLoginFormValid || loading"
                    :dark="isLoginFormValid"
                    data-cy="btn_login"
                  >
                    {{ loading ? "Logging in..." : "Login" }}
                  </v-btn>

                  <div class="mt-6">
                    <span
                      class="ml-2 blue_on_hover"
                      style="
                        cursor: pointer;
                        width: 128px;
                        display: inline-block;
                      "
                      @click="login_rp = 'fp'"
                      data-cy="href_forgot_password"
                    >
                      Forgot password?
                    </span>
                  </div>
                </v-form>
              </v-container>

              <!-- Loading Indicator -->
              <v-container v-else-if="login_rp === 'login' && loading">
                <v-progress-circular
                  class="mt-4"
                  indeterminate
                  size="86"
                  color="primary"
                ></v-progress-circular>
              </v-container>

              <!-- Password Reset Form -->
              <v-container v-else-if="login_rp === 'fp'">
                <v-stepper v-model="stepperCount" class="elevation-0">
                  <v-stepper-header class="elevation-0">
                    <v-stepper-step
                      :complete="stepperCount > 1"
                      step="1"
                      color="primary_blue"
                    ></v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step
                      step="2"
                      color="primary_blue"
                    ></v-stepper-step>
                  </v-stepper-header>

                  <v-stepper-items>
                    <!-- Enter email step -->
                    <v-stepper-content step="1">
                      <p>Enter your email</p>
                      <v-form v-model="isFormOneValid">
                        <v-text-field
                          v-model="email"
                          label="Email"
                          outlined
                          type="email"
                          clearable
                          :rules="[validateEmail]"
                          color="primary_blue"
                        />

                        <v-btn
                          color="primary_blue"
                          @click="handlePasswordReset"
                          large
                          :disabled="!isFormOneValid || loading"
                          :dark="isFormOneValid"
                        >
                          Send Reset Link
                        </v-btn>
                      </v-form>

                      <p
                        class="mt-4 blue_on_hover"
                        style="cursor: pointer"
                        @click="login_rp = 'login'"
                      >
                        Back to login
                      </p>
                    </v-stepper-content>
                  </v-stepper-items>
                </v-stepper>
              </v-container>
            </v-card>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Error/Success Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { validateEmail, validateNotEmpty } from "@/utilities/validation_rules";

export default {
  name: "Login",

  data() {
    return {
      selected_image: 0,
      email: "",
      password: "",
      visible: false,
      isLoginFormValid: false,
      isFormOneValid: false,
      login_rp: "login",
      loading: false,
      stepperCount: 1,
      snackbar: {
        show: false,
        text: "",
        color: "error",
      },
    };
  },

  computed: {
    loginError() {
      return this.$store.state.auth.loginError;
    },
  },

  methods: {
    validateEmail,
    validateNotEmpty,

    async handleLogin() {
      if (!this.isLoginFormValid) return;

      this.loading = true;
      try {
        await this.$store.dispatch("auth/login", {
          email: this.email,
          password: this.password,
        });

        // Redirect after successful login
        this.$router.push("/");
      } catch (error) {
        this.showSnackbar(error.message, "error");
      } finally {
        this.loading = false;
      }
    },

    async handlePasswordReset() {
      if (!this.isFormOneValid) return;

      this.loading = true;
      try {
        await this.$store.dispatch("auth/send_password_reset", this.email);
        this.showSnackbar("Password reset email sent", "success");
        this.login_rp = "login";
      } catch (error) {
        this.showSnackbar(error.message, "error");
      } finally {
        this.loading = false;
      }
    },

    showSnackbar(text, color = "error") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
  },

  created() {
    this.selected_image = Math.floor(Math.random() * 12) + 1;
  },

  watch: {
    loginError(newError) {
      if (newError) {
        this.showSnackbar(newError);
      }
    },

    login_rp() {
      this.selected_image = Math.floor(Math.random() * 12) + 1;
      this.email = "";
      this.password = "";
      this.visible = false;
      if (this.$refs["form-login"]) {
        this.$refs["form-login"].reset();
      }
    },
  },
};
</script>

<style scoped>
.bg {
  background-color: #0090a4;
  height: 100%;
}
#main_login_image1 {
  background: rgba(0, 0, 0, 0) url("../assets/login_images/login-image01.png")
    repeat scroll 0% 0%;
}
#main_login_image2 {
  background: rgba(0, 0, 0, 0) url("../assets/login_images/login-image02.png")
    repeat scroll 0% 0%;
}
#main_login_image3 {
  background: rgba(0, 0, 0, 0) url("../assets/login_images/login-image03.png")
    repeat scroll 0% 0%;
}
#main_login_image4 {
  background: rgba(0, 0, 0, 0) url("../assets/login_images/login-image04.png")
    repeat scroll 0% 0%;
}
#main_login_image5 {
  background: rgba(0, 0, 0, 0) url("../assets/login_images/login-image05.png")
    repeat scroll 0% 0%;
}
#main_login_image6 {
  background: rgba(0, 0, 0, 0) url("../assets/login_images/login-image06.png")
    repeat scroll 0% 0%;
}
#main_login_image7 {
  background: rgba(0, 0, 0, 0) url("../assets/login_images/login-image07.png")
    repeat scroll 0% 0%;
}
#main_login_image8 {
  background: rgba(0, 0, 0, 0) url("../assets/login_images/login-image08.png")
    repeat scroll 0% 0%;
}
#main_login_image9 {
  background: rgba(0, 0, 0, 0) url("../assets/login_images/login-image09.png")
    repeat scroll 0% 0%;
}
#main_login_image10 {
  background: rgba(0, 0, 0, 0) url("../assets/login_images/login-image10.png")
    repeat scroll 0% 0%;
}
#main_login_image11 {
  background: rgba(0, 0, 0, 0) url("../assets/login_images/login-image11.png")
    repeat scroll 0% 0%;
}

#main_login_image12 {
  background: rgba(0, 0, 0, 0) url("../assets/login_images/login-image12.png")
    repeat scroll 0% 0%;
}
</style>